import {UNASSIGNED, portfolioAcronym, portfolioFullName} from './portfolios';
import type {Domain} from '../components/dashboard/types';

/**
 * The unscoped selection. Rendered as the leading "All" pill for administrators spanning more than one
 * bucket, and otherwise unrendered — but it is also the internal state before data loads, and the
 * resting selection for a caller with no portfolio entitlement at all (a domain-owner), whose data is
 * passed through unfiltered. Distinct from a portfolio key so it can never collide.
 */
export const ALL = '__all__';

/** One pill in the switcher. */
export interface PortfolioOption {
  /** A portfolio key, the reserved UNASSIGNED bucket, or ALL (admins only). */
  value: string;
  /** Pill label: the portfolio acronym ("ETE"), "Unassigned", or "All". */
  label: string;
  /** Hex colour for the pill's dot. Undefined for the ALL pill, which has no dot. */
  color?: string;
  /** Hex colour for the pill's label while active — a contrast-safe darkening of `color`. */
  labelColor?: string;
}

/** Fixed palette pinned to the reference screenshot; anything else falls through to a stable hash. */
const PINNED_COLORS: Record<string, string> = {
  ete: '#3B82F6',
  cads: '#38BDF8',
  ers: '#DC2626',
  et: '#7C3AED'
};

const FALLBACK_PALETTE = ['#0D9488', '#D97706', '#DB2777', '#4F46E5', '#65A30D', '#0891B2'];

/** Deterministic colour for a portfolio id, stable across sessions without a stored colour field. */
export function pillColor(portfolioId: string): string {
  if (portfolioId === UNASSIGNED) return '#94A3B8';
  if (PINNED_COLORS[portfolioId]) return PINNED_COLORS[portfolioId];
  let hash = 0;
  for (let i = 0; i < portfolioId.length; i++) {
    hash = (hash * 31 + portfolioId.charCodeAt(i)) >>> 0;
  }
  return FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length];
}

/**
 * The active pill's label colour: `pillColor` darkened until it clears WCAG AA (4.5:1) against the
 * white pill. The dot keeps the bright brand colour, but several portfolio colours are far too light
 * to set text in — `cads` (#38BDF8) is about 2.1:1 on white. Scaling the channels is deliberately
 * mechanical so it holds for portfolios added later, including hash-palette fallbacks, rather than
 * relying on a hand-maintained table that a new portfolio would silently escape.
 */
export function pillLabelColor(portfolioId: string): string {
  const hex = pillColor(portfolioId);
  const channels = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  const darkened = channels.map(c => Math.round(c * 0.62));
  return `#${darkened.map(c => c.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/** Heading for the currently selected scope. */
export function headingFor(selection: string): string {
  if (selection === ALL) return 'All Portfolios';
  if (selection === UNASSIGNED) return 'Unassigned';
  return portfolioFullName(selection);
}

/**
 * Builds the switcher from the caller's REACHABLE portfolios (`GET /portfolio` → `portfolios`), not
 * from the portfolios observed on `domains`. Every portfolio the caller can reach gets a pill
 * regardless of how many there are or whether it currently holds any domains — an empty portfolio
 * renders an empty dashboard rather than vanishing from the header. `domains` is consulted only to
 * decide whether the "Unassigned" bucket is non-empty, since that bucket is a reserved key rather
 * than a real portfolio entity.
 *
 * The Unassigned pill is NOT admin-only: a caller granted an individual domain that has not been
 * classified into a portfolio needs somewhere to reach it, and without a pill it would be fetched
 * but unselectable. It stays gated on the bucket actually holding a domain the caller can see, so a
 * portfolio-viewer with no unclassified domains never sees it. For an admin it means "every
 * unclassified domain"; for anyone else, "the unclassified domains you can reach".
 *
 * The aggregate "All" option is administrators only, and only when it would aggregate more than one
 * bucket. Cross-portfolio roll-up is an oversight view, not a leader's: a leader entitled to two
 * portfolios is accountable for each on its own terms, whereas an admin needs the whole estate at
 * once. It leads the list so admins land on it by default (`resolveSelection`).
 *
 * Returns no options only when there is nothing portfolio-shaped to switch between: a domain-owner
 * grant conveys access to domains, not to a portfolio.
 */
export function portfolioOptions(entitled: string[], domains: Domain[], isAdmin: boolean): PortfolioOption[] {
  const real = [...new Set(entitled.filter(p => p && p !== UNASSIGNED))].sort((a, b) =>
    portfolioAcronym(a).localeCompare(portfolioAcronym(b))
  );
  const showUnassigned = domains.some(d => d.portfolio === UNASSIGNED);

  if (real.length === 0 && !showUnassigned) return [];

  const options: PortfolioOption[] = [];
  if (isAdmin && real.length + (showUnassigned ? 1 : 0) > 1) options.push({value: ALL, label: 'All'});
  real.forEach(key =>
    options.push({value: key, label: portfolioAcronym(key), color: pillColor(key), labelColor: pillLabelColor(key)})
  );
  if (showUnassigned) {
    options.push({
      value: UNASSIGNED,
      label: 'Unassigned',
      color: pillColor(UNASSIGNED),
      labelColor: pillLabelColor(UNASSIGNED)
    });
  }
  return options;
}

/**
 * Guarantees exactly one valid selection at all times (FR-005, FR-012). Falls back to the first pill —
 * "All" for an admin with more than one bucket, otherwise the first portfolio. With no pills at all
 * the selection rests at ALL and a domain-owner sees their domains unfiltered.
 */
export function resolveSelection(current: string, options: PortfolioOption[]): string {
  if (options.some(o => o.value === current)) return current;
  return options[0]?.value ?? ALL;
}

/** Distinct non-empty service areas referenced by the domains in view (FR-010). */
export function visibleServiceAreaCount(domains: Domain[]): number {
  const areas = new Set<string>();
  for (const d of domains) if (d.area) areas.add(d.area);
  return areas.size;
}
